import {
  CertificateGenerationDocumentId,
  type TCertificateGenerationRequest,
  type TCertificateGenerationType,
  type TSupervisorApplicationsRequest,
} from '~/store/covenants/types.ts';

export const CERTIFICATE_GENERATION_ENDPOINTS = {
  stateAidReceived: 'certificates/state-aid-received',
  financialCondition: 'certificates/financial-condition',
  incomeStructureByKved: 'certificates/income-structure-by-kved',
} as const satisfies Record<TCertificateGenerationType, string>;

export const CERTIFICATE_GENERATION_DOCUMENT_TYPES = {
  [CertificateGenerationDocumentId.StateAidReceived]: 'stateAidReceived',
} as const satisfies Partial<Record<CertificateGenerationDocumentId, TCertificateGenerationType>>;

export const buildCertificateGenerationRequest = (
  certificateType: TCertificateGenerationType,
  { contragentId, applicationId, text }: TCertificateGenerationRequest
) => ({
  url: `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/${CERTIFICATE_GENERATION_ENDPOINTS[certificateType]}`,
  body: { text },
});

export const getCertificateGenerationResponseFilename = (
  contentDisposition?: string
): string | null => {
  if (!contentDisposition) {
    return null;
  }

  const encodedFilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

  if (encodedFilenameMatch?.[1]) {
    return decodeURIComponent(encodedFilenameMatch[1]);
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

  return filenameMatch?.[1] ?? null;
};

export const getRequestErrorStatus = (error: unknown): number | undefined => {
  const normalizedError = error as
    | {
        status?: number;
        response?: { status?: number };
        data?: { status?: number };
      }
    | undefined;

  return (
    normalizedError?.status ?? normalizedError?.response?.status ?? normalizedError?.data?.status
  );
};

export const buildSupervisorApplicationsParams = ({
  page,
  size,
  fromDate,
  toDate,
  search,
  assignee,
  segmentId,
  status,
  sort,
}: TSupervisorApplicationsRequest) => {
  const params = new URLSearchParams();

  params.append('page', String(page));
  params.append('size', String(size));
  params.append('fromDate', fromDate);
  params.append('toDate', toDate);

  if (search?.trim()) {
    params.append('search', search.trim());
  }

  assignee?.forEach((assigneeValue) => {
    if (assigneeValue.trim()) {
      params.append('assignee', assigneeValue.trim());
    }
  });

  segmentId?.forEach((segmentIdValue) => {
    if (segmentIdValue.trim()) {
      params.append('segmentId', segmentIdValue.trim());
    }
  });

  status?.forEach((statusValue: NonNullable<TSupervisorApplicationsRequest['status']>[number]) => {
    params.append('status', statusValue);
  });

  sort?.forEach((sortValue) => {
    params.append('sort', sortValue);
  });

  return params;
};
